import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { powerLocationById } from '../../data/power';
import { countdownLabel, journeyVerdict } from '../../utils/power';
import { ConfidenceLine, FreshnessLine, IconTile, LOCATION_ICON, PowerStatusPill, PowerSkeleton, powerIcons } from './PowerPrimitives';
import type { PowerStatus } from '../../types/power';
export const BEFORE_YOU_GO_ART = 'https://cdn.magicpatterns.com/patterns/generated-images/693e37ca-b6bd-4515-96bc-de67acf9d603.jpg';

/**
 * Before you go — the emotional centre of Power.
 *
 * It answers one question in one glance: is there light where I am going? The
 * three rows are the person's own places in the order the journey happens, and
 * the verdict beneath them states the weakest thing found rather than averaging
 * into something reassuring.
 */
export function BeforeYouGo() {
  const {
    homeLocationId,
    campusLocationId,
    next,
    getLocationStatus,
    offline
  } = usePower();
  const rows = [{
    id: homeLocationId,
    label: 'Your accommodation'
  }, {
    id: campusLocationId,
    label: 'Campus'
  }, ...(next?.locationId ? [{
    id: next.locationId,
    label: 'Next class'
  }] : [])];
  const statuses = rows.map(row => getLocationStatus(row.id));
  const verdict = journeyVerdict(statuses);
  const heading = next ? next.laterInWeek ? `Your next class is ${next.day}, ${next.start}` : `Your next class starts in ${countdownLabel(next.startsInMinutes)}` : 'No more classes scheduled this week';
  return <section aria-labelledby="before-you-go-heading" className="relative overflow-hidden rounded-2xl border border-line bg-surface shadow-panel" data-id="element-4131" data-mp="11t3ch6clx6m1">
      {/* The illustration sits behind the content on wide cards and below the
          rows on narrow ones, so it never competes with the answer. */}
      <img src={BEFORE_YOU_GO_ART} alt="" aria-hidden className="pointer-events-none absolute bottom-0 right-0 hidden h-[78%] w-auto select-none object-contain object-bottom opacity-95 sm:block" data-id="element-4132" data-mp="e2g4kl2oygzh" />
      <div className="relative p-5 sm:p-6" data-id="element-4133" data-mp="1cdhbgjvwapf5">
        <div className="max-w-[62%]" data-id="element-4134" data-mp="1r4r9wchosqgz">
          <h2 id="before-you-go-heading" className="text-h4 font-semibold text-ink" data-id="element-4135" data-mp="1r3ok67w3ou2n">
            Before you go
          </h2>
          <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-4136" data-mp="yfsqlc1tvia3">{heading}</p>
        </div>

        <ul className="mt-4 max-w-[26rem] space-y-2.5" data-id="element-4137" data-mp="yhwv9w7jwlbf">
          {rows.map((row, index) => {
          const location = powerLocationById(row.id);
          const status = statuses[index];
          if (!location) return null;
          return <li key={`${row.id}-${row.label}`} className="flex items-center gap-3" data-id="element-4138" data-mp="13du6jo3yj1hc" data-mpi={index}>
                <IconTile name={LOCATION_ICON[location.kind]} size="sm" data-id="element-4139" data-mp="2iuw1ow7s0fs8" />
                <div className="min-w-0 flex-1" data-id="element-4140" data-mp="2eu2clzilkuxr">
                  <p className="truncate text-body-sm font-medium text-ink" data-id="element-4141" data-mp="2etiw8n8lielu">
                    {row.label === 'Next class' ? location.name : row.label}
                  </p>
                  {status.state === 'unknown' ? <p className="text-caption text-ink-subtle" data-id="element-4142" data-mp="2raldddy1zfda">No recent reports</p> : status.confidence === 'high' ? <ConfidenceLine confidence={status.confidence} data-id="element-4143" data-mp="2hdozespdy11c" /> : <FreshnessLine at={status.lastConfirmedAt} data-id="element-4144" data-mp="353jcnbdm9rm4" />}
                </div>
                <PowerStatusPill state={status.state} data-id="element-4145" data-mp="3a3ffv8ehpf0m" />
              </li>;
        })}
        </ul>

        {offline && <p className="mt-4 max-w-[26rem] rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-caption text-warn-800" data-id="element-4146" data-mp="2vdqa0zvgcmu0">
            Connection unavailable. Showing the last known power status.
          </p>}

        {/* On narrow screens the illustration moves here, at a size that does not
            eat the screen — the answer stays above it. */}
        <img src={BEFORE_YOU_GO_ART} alt="A student walking across campus" className="mt-4 h-32 w-full rounded-xl object-cover object-center sm:hidden" data-id="element-4147" data-mp="255ltiz6wfu1a" />

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-id="element-4148" data-mp="1v6mmtm5p28gx">
          <p className={`text-body font-semibold ${verdict.tone === 'ok' ? 'text-ok-800' : verdict.tone === 'warn' ? 'text-warn-800' : 'text-ink-muted'}`} data-id="element-4149" data-mp="1q8b9h1g6xfty">
            {verdict.message}
          </p>
          <Link to="/power/before-you-go" className="inline-flex min-h-touch items-center gap-2 rounded-xl bg-brand-700 px-4 py-2.5 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 focus-visible:outline-none" data-id="element-4150" data-mp="2e58lyvhnhpi7">
            View details
            <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-4151" data-mp="31ut5bfhpuos0" />
          </Link>
        </div>
      </div>
    </section>;
}
export function BeforeYouGoSkeleton() {
  return <div className="rounded-2xl border border-line bg-surface p-5 shadow-panel sm:p-6" data-id="element-4152" data-mp="3e5aqgbf0ykxe">
      <PowerSkeleton className="h-6 w-40" data-id="element-4153" data-mp="3j56s4409209s" />
      <PowerSkeleton className="mt-2 h-4 w-56" data-id="element-4154" data-mp="3j3c7kqsw4a68" />
      <div className="mt-5 space-y-3" data-id="element-4155" data-mp="3o3qhkee1xu6w">
        <PowerSkeleton className="h-11 w-full max-w-[26rem]" data-id="element-4156" data-mp="3o4t7ok3jvslr" />
        <PowerSkeleton className="h-11 w-full max-w-[26rem]" data-id="element-4157" data-mp="3t3csuiabl0d2" />
        <PowerSkeleton className="h-11 w-full max-w-[26rem]" data-id="element-4158" data-mp="3t2usb9gmjzzc" />
      </div>
      <PowerSkeleton className="mt-5 h-11 w-40" data-id="element-4159" data-mp="xzhxy0nej7i" />
    </div>;
}

/** One reusable status row, used by the overview and the full-screen view. */
export function PowerStatusRow({
  label,
  status,
  icon
}: {
  label: string;
  status: PowerStatus;
  icon: keyof typeof powerIcons;
}) {
  return <div className="flex items-center gap-3" data-id="element-4160" data-mp="fphv7o24hzn6">
      <IconTile name={icon} size="sm" data-id="element-4161" data-mp="fmdmcddpqfq6" />
      <div className="min-w-0 flex-1" data-id="element-4162" data-mp="kocojjxpv6lj">
        <p className="truncate text-body-sm font-medium text-ink" data-id="element-4163" data-mp="kl6ya1dcpyo8">{label}</p>
        <FreshnessLine at={status.lastConfirmedAt} data-id="element-4164" data-mp="3r5bfrotmvdop" />
      </div>
      <PowerStatusPill state={status.state} data-id="element-4165" data-mp="3m5yv9aq8iipa" />
    </div>;
}